// Mirrors reporting-hub's PreflightPage Tab 3 "Calculation": a Calculate
// button, the real Category-sidebar/Group-panel results view (generic table
// w/ formula tooltips, Various Stats KPI grid, Cash Positions/NAV matrices,
// editable Hard-Coded Values dialog), Control Checks, "Generate Reporting",
// and an "Exports" menu (LoanTape / CashFlows xlsx+parquet / Investment
// Summary per scope). Calculate, Generate Reporting and exports are jobs
// (see jobs.js); progress shows in the wizard's job banner.

import React, { useEffect, useRef, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import CalculationResultsView from '../calculation/CalculationResultsView';
import ControlChecksPanel from '../ControlChecksPanel';
import EmptyState from '../../common/EmptyState';
import { API, apiErrorDetail, downloadUrl, runExport, useWizardJob } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const INVESTMENT_SUMMARY_SCOPES = [
  { scope: 'fund', label: 'Fund' },
  { scope: 'FINBEE', label: 'Finbee' },
  { scope: 'VAURAUS', label: 'Vauraus' },
  { scope: 'SAVY', label: 'Savy' },
  { scope: 'MOZZENO', label: 'Mozzeno' },
];

const CalculationTab = ({ reportingId, status, onStageChanged, setLoading }) => {
  const [calculationResult, setCalculationResult] = useState(null);
  const [controlChecksResult, setControlChecksResult] = useState(null);
  const [error, setError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'calculate' | 'report' | 'export'
  const [refreshKey, setRefreshKey] = useState(0);
  const exportMenuRef = useRef(null);
  const { runJob, busy } = useWizardJob();

  const isCalculated = status && (status.stage === 'calculated' || status.stage === 'reported');
  const hasCashflows = !!(status && status.inputs_summary && status.inputs_summary.cashflows);

  useEffect(() => {
    if (!reportingId || !isCalculated) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axiosWithAuth.get(`${API}/reportings/${reportingId}/calculation`),
      axiosWithAuth.get(`${API}/reportings/${reportingId}/control-checks`),
    ])
      .then(([calcRes, controlRes]) => {
        if (cancelled) return;
        setCalculationResult(calcRes.data);
        setControlChecksResult(controlRes.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[CalculationTab] fetch error:', errorMessage(err));
        setError(errorMessage(err, 'Failed to load calculation / control checks.'));
      })
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingId, isCalculated, refreshKey]);

  const refreshResults = () => setRefreshKey((k) => k + 1);

  const handleCalculate = async () => {
    setPendingAction('calculate');
    setError(null);
    try {
      await runJob(reportingId, 'calculate', {});
      await onStageChanged();
      refreshResults();
    } catch (err) {
      if (err.cancelled) return;
      console.error('[handleCalculate] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Calculate failed.'));
    }
    setPendingAction(null);
  };

  const handleGenerateReport = async () => {
    setPendingAction('report');
    setError(null);
    try {
      await runJob(reportingId, 'report', { scope: 'fund' });
      await onStageChanged();
    } catch (err) {
      if (err.cancelled) return;
      console.error('[handleGenerateReport] error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Generate Report failed.'));
    }
    setPendingAction(null);
  };

  const withExport = async (fn) => {
    setPendingAction('export');
    setExportError(null);
    try {
      await fn();
    } catch (err) {
      if (err.cancelled) return;
      console.error('[CalculationTab] export error:', err.detail || errorMessage(err));
      setExportError(apiErrorDetail(err, 'Export failed.'));
    }
    setPendingAction(null);
  };

  const exportLoantape = () => withExport(() => runExport(runJob, reportingId, { kind: 'loantape' }));

  // A stored CashFlows file already in the requested format downloads
  // straight from S3; otherwise (409 job_required) an export job converts it.
  const exportCashflows = (format) =>
    withExport(async () => {
      try {
        const res = await axiosWithAuth.get(`${API}/reportings/${reportingId}/cashflows/direct-export`, { params: { format } });
        downloadUrl(res.data.url, res.data.filename);
        return;
      } catch (err) {
        if (!(err?.response?.status === 409 && err.response.data?.job_required)) throw err;
      }
      await runExport(runJob, reportingId, { kind: 'cashflows', format });
    });

  const exportInvestmentSummary = (scope) =>
    withExport(() => runExport(runJob, reportingId, { kind: 'investment_summary', scope }));

  const exportMenuItems = [
    {
      label: 'LoanTape',
      items: [{ label: 'LoanTape (xlsx)', icon: 'pi pi-table', command: exportLoantape }],
    },
    {
      label: 'CashFlows',
      items: [
        { label: 'CashFlows (xlsx)', icon: 'pi pi-file-excel', command: () => exportCashflows('xlsx'), disabled: !hasCashflows },
        { label: 'CashFlows (parquet)', icon: 'pi pi-file', command: () => exportCashflows('parquet'), disabled: !hasCashflows },
      ],
    },
    {
      label: 'Investment Summary',
      items: INVESTMENT_SUMMARY_SCOPES.map(({ scope, label }) => ({
        label,
        icon: 'pi pi-chart-bar',
        command: () => exportInvestmentSummary(scope),
        disabled: !isCalculated,
      })),
    },
  ];

  return (
    <div>
      <div className="ex-section-header">
        <h3 className="ex-section-title">Calculation</h3>
      </div>
      {error && <div className="ex-inline-error mb-3">{error}</div>}

      <div className="cr-card cr-action-bar mb-4">
        <div className="ex-toolbar-group">
          <Button
            label="Calculate"
            icon="pi pi-calculator"
            className={isCalculated ? 'p-button-outlined' : undefined}
            onClick={handleCalculate}
            disabled={busy}
            loading={pendingAction === 'calculate'}
          />
          <Button
            label="Generate Reporting"
            icon="pi pi-file-pdf"
            className={isCalculated ? undefined : 'p-button-outlined'}
            onClick={handleGenerateReport}
            disabled={!isCalculated || busy}
            loading={pendingAction === 'report'}
          />
        </div>
        <div className="cr-action-bar-text">
          {status && status.stage === 'reported'
            ? 'Reporting generated - open the Reporting tab. Re-run Calculate / Generate Reporting after changing inputs.'
            : isCalculated
            ? 'Review the results and control checks below, then Generate Reporting to unlock the Reporting tab.'
            : 'Run Calculate on the validated data - results and control checks appear below.'}
        </div>
        <Menu model={exportMenuItems} popup ref={exportMenuRef} />
        <Button
          label="Exports"
          icon={pendingAction === 'export' ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
          className="p-button-secondary p-button-outlined"
          disabled={!reportingId || busy}
          onClick={(e) => exportMenuRef.current.toggle(e)}
        />
      </div>
      {exportError && <div className="ex-inline-error mb-3">{exportError}</div>}

      {isCalculated ? (
        <>
          <CalculationResultsView
            reportingId={reportingId}
            calculationResult={calculationResult}
            setLoading={setLoading}
            onRecalculated={() => {
              refreshResults();
              onStageChanged();
            }}
          />
          <div className="ex-section cr-control-checks mt-4">
            <div className="ex-section-header">
              <h4 className="ex-section-title">Control Checks</h4>
            </div>
            <ControlChecksPanel result={controlChecksResult} />
          </div>
        </>
      ) : (
        <EmptyState icon="pi-calculator" title="Run Calculate to see results." compact />
      )}
    </div>
  );
};

export default CalculationTab;
